import { useState, useMemo } from 'react'
import type { FC } from 'react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import type { Word } from '../../types/wordCollection'
import { SetAsideItem } from './SetAsideItem'

interface SetAsideListProps {
  words: Word[]
  searchQuery: string
  onReactivate: (wordId: string) => void
  onUpdateNotes: (wordId: string, notes: string) => void
}

/**
 * SetAsideList - Collapsible list of set-aside words with search filtering
 *
 * Displays set-aside words in a collapsed-by-default section. Shows count in header,
 * filters by search query, and renders SetAsideItem components for each word.
 * Reactivate and notes actions are passed through to parent.
 */
export const SetAsideList: FC<SetAsideListProps> = ({
  words,
  searchQuery,
  onReactivate,
  onUpdateNotes
}) => {
  const [collapsed, setCollapsed] = useState(true)

  const handleToggle = () => setCollapsed(prev => !prev)

  // Filter words by search query (case-insensitive)
  const filteredWords = useMemo(() => {
    if (!searchQuery) return words

    const normalizedQuery = searchQuery.toLowerCase()
    return words.filter(word =>
      word.word.toLowerCase().includes(normalizedQuery)
    )
  }, [words, searchQuery])

  return (
    <div
      className="rounded-2xl border border-gray-200 bg-white shadow-sm transition"
      data-testid="set-aside-list"
    >
      {/* Header with collapse toggle */}
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left transition bg-white"
        onClick={handleToggle}
        aria-expanded={!collapsed}
        aria-controls="set-aside-list-content"
        data-testid="set-aside-list-header"
      >
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500">
            Set Aside
          </p>
          <p className="text-[0.65rem] text-gray-500">
            {filteredWords.length} word{filteredWords.length === 1 ? '' : 's'}
          </p>
        </div>
        <ChevronDownIcon
          className={`h-4 w-4 text-gray-500 transition-transform ${collapsed ? '' : 'rotate-180'}`}
          aria-hidden
          data-testid="set-aside-list-chevron"
        />
      </button>

      {/* Content - collapsed by default */}
      {!collapsed && (
        <div
          id="set-aside-list-content"
          className="space-y-2 px-4 pb-4 pt-2 transition"
          data-testid="set-aside-list-content"
        >
          {filteredWords.length === 0 ? (
            <p className="text-[0.65rem] text-gray-400">
              {searchQuery ? 'No matching words' : 'No words set aside'}
            </p>
          ) : (
            filteredWords.map(word => (
              <SetAsideItem
                key={word.id}
                word={word}
                onReactivate={() => onReactivate(word.id)}
                onUpdateNotes={(notes) => onUpdateNotes(word.id, notes)}
              />
            ))
          )}
        </div>
      )}
    </div>
  )
}
